/**
 * How long to wait before trying the gateway again.
 *
 * Exponential with jitter (real-time updates §4). The jitter is not decoration: a gateway
 * replica that restarts drops every socket it held at the same instant, and a fixed schedule would
 * bring all of them back in the same millisecond — the thundering herd that keeps the replica down.
 *
 * The numbers are: first retry inside half a second, so a blip is invisible to the agent; a ceiling
 * of thirty seconds, so a workspace left open through a long outage still comes back by itself and
 * does not need a reload.
 */
export const firstDelayMs = 500;
export const maxDelayMs = 30_000;

/**
 * The delay before attempt `attempt` (the first reconnection is attempt 1).
 *
 * `random` is a parameter so a test can be deterministic without pinning the schedule: what the
 * tests assert is that the delay is inside its band and that the band grows, never that the third
 * retry waits exactly two seconds.
 */
export function backoffDelayMs(attempt: number, random: () => number = Math.random): number {
  const ceiling = Math.min(firstDelayMs * 2 ** Math.max(0, attempt - 1), maxDelayMs);
  // Half the ceiling plus a random half, rather than anywhere between zero and it: spreading the
  // herd is the point, but a client that rolls near zero every time retries almost immediately on
  // a gateway that is still down, and the first attempt is already fast enough to hide a blip.
  return Math.max(1, Math.round(ceiling * (random() * 0.5 + 0.5)));
}
